import { z } from 'zod';
// =============================================================================
// Recipient particulars on a received tax invoice (VAT Act s20(4) / s20(5))
// =============================================================================
//
// A supplier's tax invoice must describe US, the recipient, before it supports an
// input-tax deduction. This module holds the vocabulary for checking that the
// details they printed actually match our own record: the per-particular match
// verdict, the document-level roll-up, and the statutory threshold that decides
// whether the particulars were required in the first place.
//
// Extraction reads the printed values; the comparison against our BusinessEntity
// is deterministic and lives in the backend extraction domain. Only the vocabulary
// is shared, because both the expense response and the extraction response carry
// the verdict and the Bill UI renders it.
/**
 * Consideration (VAT-inclusive) at or above which a tax invoice must carry the
 * FULL particulars of s20(4), including the recipient's name, address, and
 * (where the recipient is a registered vendor) VAT registration number. Below
 * it, s20(5) permits an abridged tax invoice, which requires NO recipient
 * particulars at all.
 *
 * R5 000, in minor units. Set by section 29 of the Tax Administration Amendment
 * Act 21 of 2012, which raised the abridged-invoice ceiling from R3 000.
 *
 * This threshold is why absent recipient details are not automatically a
 * compliance failure: a till slip for a R200 purchase is a lawful abridged tax
 * invoice precisely because it names nobody. Treating every such receipt as
 * non-compliant would bury the real signal in noise.
 */
export const FULL_TAX_INVOICE_THRESHOLD_MINOR_UNITS = 500_000;
/**
 * Whether a supply at this consideration requires the full s20(4) particulars
 * (and therefore the recipient's details) rather than the abridged s20(5) set.
 *
 * The Act tests the consideration in money for the supply, i.e. the
 * VAT-inclusive total. `null` (an unreadable total) is treated as requiring the
 * full particulars: an unknown amount must not silently excuse a missing
 * particular, and the conservative reading is the one an auditor would take.
 */
export function requiresFullTaxInvoiceParticulars(totalMinorUnits) {
    if (totalMinorUnits === null)
        return true;
    return totalMinorUnits >= FULL_TAX_INVOICE_THRESHOLD_MINOR_UNITS;
}
/**
 * Outcome of comparing ONE printed recipient particular against our own record.
 *
 * The two "absent" cases are deliberately distinct rather than collapsed into a
 * single "cannot compare": `ABSENT_ON_DOCUMENT` is the supplier's omission and
 * can be a compliance failure, whereas `ABSENT_ON_RECORD` is a gap in OUR data
 * and never is - it means we have nothing to check against, so the supplier is
 * owed no blame for it.
 */
export const RecipientParticularMatchSchema = z.enum([
    'MATCH',
    'MISMATCH',
    'ABSENT_ON_DOCUMENT',
    'ABSENT_ON_RECORD',
]);
/**
 * Document-level roll-up of the recipient-particulars check.
 *
 * - `MATCH`: every particular the Act requires here is present and agrees.
 * - `MISMATCH`: a printed particular CONTRADICTS our record. The alarming case -
 *   an invoice made out to a different entity (mis-sent, or a personal expense
 *   being passed off as the firm's).
 * - `INCOMPLETE`: nothing contradicts, but a particular s20(4) requires is
 *   missing. An administrative compliance gap, not a red flag.
 * - `NOT_ADDRESSED`: the document carries no recipient particulars at all, on a
 *   supply that required them.
 * - `NOT_REQUIRED`: below the s20(5) threshold and nothing was printed, so the
 *   absence is lawful. NOT a failure.
 * - `UNVERIFIABLE`: our own record has nothing to compare against (no linked
 *   business entity, or every particular absent on our side).
 *
 * Precedence when several could apply: MISMATCH > NOT_ADDRESSED > INCOMPLETE >
 * MATCH. A contradiction outranks the threshold, so a R200 receipt made out to
 * someone else still reports MISMATCH rather than NOT_REQUIRED - the threshold
 * governs whether ABSENCE is excusable, never whether a contradiction matters.
 */
export const RecipientParticularsStatusSchema = z.enum([
    'MATCH',
    'MISMATCH',
    'INCOMPLETE',
    'NOT_ADDRESSED',
    'NOT_REQUIRED',
    'UNVERIFIABLE',
]);
/**
 * Full result of the recipient-particulars check on one received document.
 *
 * Stored on the extraction row (which also holds the printed values themselves)
 * and rolled up onto the expense as a single queryable status column.
 */
export const RecipientParticularsValidationSchema = z.object({
    status: RecipientParticularsStatusSchema,
    /**
     * Whether s20(4)'s full particulars were required for this supply, i.e.
     * whether the consideration met FULL_TAX_INVOICE_THRESHOLD_MINOR_UNITS.
     * Carried explicitly so a reader can explain a NOT_REQUIRED verdict without
     * re-deriving it from an amount that may since have been edited.
     */
    requiresFullParticulars: z.boolean(),
    /**
     * Whether OUR firm is VAT-registered. Decides whether the recipient VAT
     * number was a required particular at all: s20(4)(c) requires it only "where
     * the recipient is a registered vendor".
     */
    recipientIsVatRegistered: z.boolean(),
    /**
     * Per-particular verdicts. `registrationNumber` is compared opportunistically
     * and is NEVER a compliance input: a company registration number is not among
     * the s20(4) particulars, so its absence cannot make a document INCOMPLETE. It
     * earns its place because a MISMATCH on it is still strong evidence the
     * document is made out to a different entity.
     */
    particulars: z.object({
        name: RecipientParticularMatchSchema,
        vatNumber: RecipientParticularMatchSchema,
        registrationNumber: RecipientParticularMatchSchema,
        address: RecipientParticularMatchSchema,
    }),
});
/**
 * Short user-facing label per roll-up status. Plain SA English, readable by a
 * bookkeeper rather than a tax practitioner - the person clearing an expense
 * queue needs to know whether to act, not which subsection was engaged.
 */
export const RECIPIENT_PARTICULARS_STATUS_LABELS = {
    MATCH: 'Addressed to you',
    MISMATCH: 'Addressed to someone else',
    INCOMPLETE: 'Missing your details',
    NOT_ADDRESSED: 'Not addressed to anyone',
    NOT_REQUIRED: 'Details not required',
    UNVERIFIABLE: 'Cannot be checked',
};
/**
 * One-line explanation per status, shown under the label. Says what it means and
 * what to do, without asserting a tax outcome we have not computed: whether SARS
 * would actually disallow the input deduction depends on facts beyond the
 * document (and on the reviewer's judgement), so the copy stops at the finding.
 */
export const RECIPIENT_PARTICULARS_STATUS_EXPLANATIONS = {
    MATCH: "This invoice carries your firm's details as they appear on your record.",
    MISMATCH: "The details on this invoice do not match your firm's. Check that it was meant for you before claiming it.",
    INCOMPLETE: 'A tax invoice for this amount must show your name, address and VAT number. Ask the supplier for a corrected invoice.',
    NOT_ADDRESSED: 'A tax invoice for this amount must be made out to you. Ask the supplier for a corrected invoice.',
    NOT_REQUIRED: 'Below R5,000 a supplier may issue an abridged tax invoice, which does not have to name you.',
    UNVERIFIABLE: "Your firm's own details are incomplete, so there is nothing to check this invoice against.",
};
/**
 * Whether a status is worth surfacing to the user as something to look at.
 *
 * `MATCH` and `NOT_REQUIRED` are both clean outcomes and stay silent.
 * `UNVERIFIABLE` DOES surface: it is a prompt to complete the firm's own
 * details, and staying quiet about it would let a firm believe its invoices are
 * being checked when nothing is.
 */
export function isRecipientParticularsConcern(status) {
    return status !== 'MATCH' && status !== 'NOT_REQUIRED';
}
